import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dropdown, Grid, GridColumn, Icon } from "semantic-ui-react";

import { Can, NavPortal, ResetFiltersButton, SearchFilter, usePermanentLocations, usePermanentRealtimeDealers } from "components";
import { CreateUserModal, FilterDatePicker, PairUsers } from "modules/UsersDashboard/components";
import { useGetUsersDashboardFilters, useUsersDashboardFilterHandlers } from "modules/UsersDashboard/components/UsersDashboardFilters/hooks";
import "modules/UsersDashboard/components/UsersDashboardFilters/UsersDashboardFilters.scss";
import { USER_TYPES, getUserDropdownOptions, useGetPairingUsersOptions, useGetUserRoleOptions, useGetVersionsOptions } from "modules/UsersDashboard/hooks";

import type { USER_STATUSES } from "modules/UsersDashboard/hooks";

export enum PAIR_USERS_OPTIONS {
  OneIAutomotive = 1,
  PlanIt,
  Dms
}

const getUserTypeValue = (isCombiUser: boolean | null, isDpo: boolean | null) => {
  if (isCombiUser === null && isDpo === null) return USER_TYPES.All;
  if (isCombiUser && !isDpo) return USER_TYPES.Combi;
  if (!isCombiUser && isDpo) return USER_TYPES.Dpo;

  return USER_TYPES.Regular;
};

export const UsersDashboardFilters = () => {
  const { t } = useTranslation();

  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showPairUsersModal, setShowPairUsersModal] = useState<PAIR_USERS_OPTIONS | null>(null);

  const { dealers } = usePermanentRealtimeDealers();
  const { locations } = usePermanentLocations();
  const {
    handleSearchChange,
    handleResetFilters,
    handleUserTypeChange,
    handleUserRolesChange,
    handleUserStatusChange,
    handleWebVersionsChange,
    handleAppVersionsChange,
    handleActiveDateToChange,
    handleActiveDateFromChange,
    handleDealerChange,
    handleLocationChange
  } = useUsersDashboardFilterHandlers();
  const {
    filters: { term, dealer_location_ids, dealer_ids, mobile_versions, web_versions, roles, is_combi_user, is_dpo, status, active_date_from, active_date_to }
  } = useGetUsersDashboardFilters();

  const { userRolesOptions } = useGetUserRoleOptions();
  const { pairUsersOptions } = useGetPairingUsersOptions();
  const { userStatusOptions, userTypeOptions } = getUserDropdownOptions(t);
  const { appVersionOptions, webVersionOptions } = useGetVersionsOptions();

  const dealerOptions = dealers
    .filter(dealer => dealer.active)
    .map(({ id, name }) => ({ key: id, value: id, text: name }))
    .sort((a, b) => a.text.localeCompare(b.text));
  const locationOptions = locations
    .filter(location => location.active && dealer_ids.includes(location.dealer_id))
    .map(({ id, name }) => ({ key: id, value: id, text: name }))
    .sort((a, b) => a.text.localeCompare(b.text));

  const handleOpenCreateUserModal = () => setShowCreateUserModal(true);
  const handleCloseCreateUserModal = () => setShowCreateUserModal(false);
  const handleOpenPairingModal = (data: PAIR_USERS_OPTIONS) => setShowPairUsersModal(data);
  const handleClosePairingModal = () => setShowPairUsersModal(null);

  return (
    <>
      <NavPortal>
        <SearchFilter value={term} handleSearchChange={handleSearchChange} />
      </NavPortal>

      <div className="filters-toolbar-container UsersDashboardFilters__first-toolbar-container">
        <div className="UsersDashboardFilters__pair-add-users-container">
          <Can I="create" the="users">
            <Button basic icon className="add-btn" onClick={handleOpenCreateUserModal}>
              <Icon className="plus" />
            </Button>
          </Can>

          <Can I="update" the="users">
            <div className="UsersDashboardFilters__pair-users-container">
              <Button icon labelPosition="left" fluid className="action-button">
                <div className="icon-text-container">
                  <Icon className="linkify pair-icon" />
                  <span className="UsersDashboardFilters__pair-user-text">{t("v8_pair_users", "Pair users")}</span>
                </div>
              </Button>
              <Dropdown
                className="button icon pair-users-dropdown"
                selectOnBlur={false}
                basic
                value={0}
                options={pairUsersOptions}
                trigger={<></>}
                onChange={(_, data) => handleOpenPairingModal(data.value as PAIR_USERS_OPTIONS)}
                disabled={dealer_location_ids.length > 1 || dealer_ids.length > 1}
              />
            </div>
          </Can>
        </div>

        <Grid columns="equal" className="UsersDashboardFilters__toolbar-grid UsersDashboardFilters__no-right-gutter">
          <GridColumn width={3} className="UsersDashboardFilters__left-gutter UsersDashboardFilters__right-gutter">
            <Dropdown
              fluid
              selection
              name="filterByRole"
              clearable
              multiple
              value={roles}
              selectOnBlur={false}
              options={userRolesOptions}
              placeholder={t("v8_filter_by_role", "Filter by role")}
              onChange={(_, { value }) => handleUserRolesChange(value as number[])}
            />
          </GridColumn>

          <GridColumn width={5} className="UsersDashboardFilters__left-gutter UsersDashboardFilters__right-gutter">
            <Dropdown
              fluid
              selection
              search
              name="dealer"
              clearable
              selectOnBlur={false}
              options={dealerOptions}
              value={dealer_ids[0] ?? ""}
              placeholder={t("v8_filter_by_dealer", "Filter by dealer")}
              onChange={(_event, data) => handleDealerChange(data.value as number | null)}
            />
          </GridColumn>

          <GridColumn width={5} className="UsersDashboardFilters__left-gutter UsersDashboardFilters__right-gutter">
            <Dropdown
              fluid
              selection
              name="location"
              clearable
              search
              multiple
              selectOnBlur={false}
              options={locationOptions}
              disabled={dealer_ids.length === 0}
              value={dealer_ids.length ? dealer_location_ids : []}
              placeholder={t("v8_filter_by_location", "Filter by location")}
              onChange={(_event, data) => handleLocationChange(data.value as number[] | null)}
            />
          </GridColumn>

          <GridColumn width={3} className="UsersDashboardFilters__left-gutter UsersDashboardFilters__no-right-gutter">
            <Dropdown
              fluid
              selection
              name="userStatus"
              value={status}
              selectOnBlur={false}
              options={userStatusOptions}
              onChange={(_, { value }) => handleUserStatusChange(value as USER_STATUSES)}
            />
          </GridColumn>
        </Grid>

        <div className="UsersDashboardFilters__no-left-gutter UsersDashboardFilters__toolbar-reset-button-container">
          <ResetFiltersButton handleReset={handleResetFilters} />
        </div>
      </div>

      <div className="filters-toolbar-container UsersDashboardFilters__second-toolbar-container">
        <Grid columns={16} className="UsersDashboardFilters__toolbar-grid">
          <GridColumn width={3} className="UsersDashboardFilters__no-left-gutter UsersDashboardFilters__right-gutter">
            <Dropdown
              fluid
              selection
              name="filterByWebVersion"
              clearable
              multiple
              value={web_versions || []}
              selectOnBlur={false}
              options={webVersionOptions}
              placeholder={t("v8_filter_by_web_version", "Filter by Web Version")}
              onChange={(_, { value }) => handleWebVersionsChange(value as string[])}
            />
          </GridColumn>

          <GridColumn width={3} className="UsersDashboardFilters__left-gutter UsersDashboardFilters__right-gutter">
            <Dropdown
              fluid
              selection
              name="filterByAppVersion"
              clearable
              multiple
              value={mobile_versions || []}
              selectOnBlur={false}
              options={appVersionOptions}
              placeholder={t("v8_filter_by_app_version", "Filter by App Version")}
              onChange={(_, { value }) => handleAppVersionsChange(value as string[])}
            />
          </GridColumn>

          <GridColumn width={7} className="UsersDashboardFilters__left-gutter UsersDashboardFilters__right-gutter">
            <div className="UsersDashboardFilters__dates-container">
              <FilterDatePicker
                clearable
                value={active_date_from}
                onChange={handleActiveDateFromChange}
                placeholder={t("v8_select_active_date_from", "Select active date from")}
              />
              <FilterDatePicker
                clearable
                value={active_date_to}
                onChange={handleActiveDateToChange}
                placeholder={t("v8_select_active_date_to", "Select active date to")}
              />
            </div>
          </GridColumn>

          <GridColumn width={3} className="UsersDashboardFilters__left-gutter UsersDashboardFilters__right-gutter">
            <Dropdown
              fluid
              selection
              name="userType"
              value={getUserTypeValue(is_combi_user, is_dpo)}
              selectOnBlur={false}
              options={userTypeOptions}
              onChange={(_, { value }) => handleUserTypeChange(value as USER_TYPES)}
            />
          </GridColumn>
        </Grid>
      </div>

      {showCreateUserModal && <CreateUserModal onClose={handleCloseCreateUserModal} />}
      {showPairUsersModal && <PairUsers pairType={showPairUsersModal} onClose={handleClosePairingModal} />}
    </>
  );
};
